import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { API, tokenStore } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Lock,
  AlertTriangle,
  Download,
  Calculator,
  Calendar,
  Save,
  Plus,
  Trash2,
  ListChecks,
  History as HistoryIcon,
  RefreshCw,
  FileDown,
} from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";

// ---------------------------------------------------------------- helpers
const fmt$ = (n, digits = 2) =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
const fmt$0 = (n) => fmt$(n, 0);
const MONTH_NAMES = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function toSundayISO(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}
function todayISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}
// Commission-effective boundary. Any Sunday before this is not enterable.
// The boundary week itself (this Sunday) is a partial week where only
// Jul 1–4 are payable.
const COMMISSION_BOUNDARY_SUNDAY = "2026-06-28";
function isValidSunday(iso) {
  if (!iso) return false;
  const d = new Date(iso + "T00:00:00");
  return !isNaN(d.getTime()) && d.getDay() === 0;
}
function isPayableSunday(iso) {
  return isValidSunday(iso) && iso >= COMMISSION_BOUNDARY_SUNDAY;
}

async function downloadPdfBlob(endpoint, body, filename) {
  const token = tokenStore.getForCurrentContext();
  const slug =
    typeof window !== "undefined"
      ? window.location.pathname.split("/").filter(Boolean)[0]
      : null;
  const qs = slug && slug !== "admin" ? `?tenant_slug=${slug}` : "";
  const resp = await fetch(`${API}${endpoint}${qs}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => "");
    throw new Error(`${resp.status}: ${txt.slice(0, 160)}`);
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------- page
export default function Comp() {
  const { user } = useAuth();
  const isReadOnly = user?.role !== "platform_admin";
  const [status, setStatus] = useState(null);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [historyBump, setHistoryBump] = useState(0);
  const bumpHistory = useCallback(() => setHistoryBump((n) => n + 1), []);

  const loadStatus = async () => {
    setLoadState({ status: "loading", error: null });
    try {
      const { data } = await api.get("/comp/v2/status");
      setStatus(data);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setStatus(null);
      setLoadState({ status: "error", error: extractApiError(err) });
    }
  };

  useEffect(() => { loadStatus(); }, []);

  if (loadState.status === "error" && loadState.error?.status === 403) {
    return (
      <div
        data-testid="comp-forbidden"
        className="bg-[#fff5f5] border border-[#f5c2c2] rounded-xl p-5 text-[#7a1c1c] text-[13.5px]"
      >
        Comp is a platform-admin-only view.
      </div>
    );
  }
  if (loadState.status !== "ok" || !status) {
    return (
      <PanelState
        status={loadState.status}
        error={loadState.error}
        onRetry={loadStatus}
        testIdPrefix="comp"
      />
    );
  }

  return (
    <div data-testid="comp-page" className="space-y-5">
      <PageHeader />
      <PrivateBanner readOnly={isReadOnly} userEmail={user?.email} />
      <TermStatusTiles status={status} />
      {!isReadOnly && <WeeklyCalculator onSaved={bumpHistory} />}
      {!isReadOnly && <BulkEntry onSaved={bumpHistory} />}
      <History bump={historyBump} readOnly={isReadOnly} />
      <MonthlyTrueup readOnly={isReadOnly} />
      <FormulaReference status={status} />
    </div>
  );
}

// ---------------------------------------------------------------- top banners / header
function PageHeader() {
  const { slug } = useParams();
  const navigate = useNavigate();
  return (
    <header>
      <div className="text-[12px] uppercase tracking-[.12em] text-[color:var(--cc-ink-muted)] font-semibold">
        Brett · COO Commission Report · Effective Jul 1, 2026
      </div>
      <div className="flex items-start justify-between mt-1 gap-4">
        <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420]">
          COO Commission Report
        </h1>
        <button
          onClick={() => navigate(`/${slug}/app/comp/tech-pay`)}
          className="shrink-0 mt-1 bg-[#0e1420] text-white rounded px-3 py-1.5 text-[12px] font-semibold hover:bg-[#1f2937]"
          data-testid="comp-tech-pay-nav-btn"
        >
          Tech Pay Statements →
        </button>
      </div>
      <p className="text-[14px] text-[color:var(--cc-ink-muted)] mt-1">
        Base $60,000/yr + 18% override on weekly revenue above the monthly baseline. Hard cap $300,000 total cash comp for the Term.
      </p>
    </header>
  );
}

function PreviewBanner() {
  return null;
}

function PrivateBanner({ readOnly, userEmail }) {
  if (readOnly) {
    return (
      <div
        className="flex items-start gap-3 bg-[#fff7ed] border border-[#f59e0b] rounded-xl p-4"
        data-testid="comp-readonly-banner"
      >
        <Lock size={16} strokeWidth={1.8} className="text-[#7c2d12] mt-0.5" />
        <div>
          <div className="text-[13.5px] font-semibold text-[#7c2d12]">Read-only view</div>
          <div className="text-[13px] text-[#7c2d12]/85">
            {userEmail ? <><span className="font-mono-data">{userEmail}</span> has </> : "You have "}
            view-only access to the COO Commission Report — weekly history, monthly true-up history, and PDF downloads.
            All write actions (compute, save, delete, mark-paid, bulk entry) are disabled on this UI and rejected server-side. All other roles remain fully excluded.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="flex items-start gap-3 bg-[#e7f4fb] border border-[#c5e0ee] rounded-xl p-4"
      data-testid="comp-private-banner"
    >
      <Lock size={16} strokeWidth={1.8} className="text-[#1c6a99] mt-0.5" />
      <div>
        <div className="text-[13.5px] font-semibold text-[#1c6a99]">Private to platform_admin</div>
        <div className="text-[13px] text-[#1c6a99]/85">
          Full read/write access. Nathanael (owner) has read-only visibility of the History + Monthly true-up + PDFs. No other tenant role can see this page.
        </div>
      </div>
    </div>
  );
}

function TermStatusTiles({ status }) {
  return (
    <section className="grid gap-4 md:grid-cols-4" data-testid="comp-term-status">
      <Tile label="Term dates" value={`${status.term_start} → ${status.term_end}`} sub={status.in_term ? "In term" : "Out of term window"} />
      <Tile label="Weeks remaining" value={status.weeks_remaining} sub={`As of ${status.today}`} />
      <Tile label="Base paid YTD (prorated)" value={fmt$0(status.base_paid_ytd)} sub={`$${Number(status.base_salary_monthly).toLocaleString()}/mo × months elapsed`} />
      <Tile label="Annual cap" value={fmt$0(status.annual_cap)} sub={`Override rate ${Math.round(Number(status.override_rate) * 100)}%`} />
    </section>
  );
}

function Tile({ label, value, sub }) {
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl p-5">
      <div className="text-[11.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">{label}</div>
      <div className="font-mono-data font-bold text-[22px] text-[#0e1420] mt-2">{value}</div>
      {sub && <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mt-1">{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- weekly calculator
function WeeklyCalculator({ onSaved }) {
  const initialSunday = useMemo(() => toSundayISO(todayISO()), []);
  const [weekStart, setWeekStart] = useState(initialSunday);
  const [revenue, setRevenue] = useState("");
  const [nonPayableRevenue, setNonPayableRevenue] = useState("0");
  const [overridePaidYtd, setOverridePaidYtd] = useState("0");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const weekIsSunday = isValidSunday(weekStart);
  const weekIsPayable = isPayableSunday(weekStart);
  const weekIsBoundary = weekStart === "2026-06-28";

  const calc = async () => {
    setErr(null); setMsg(null); setResult(null);
    if (!weekIsSunday) { setErr("Week start must be a Sunday."); return; }
    if (!weekIsPayable) {
      setErr(`Commission is effective Jul 1, 2026. Earliest enterable week is ${COMMISSION_BOUNDARY_SUNDAY} (boundary week).`);
      return;
    }
    if (!revenue || Number(revenue) < 0) { setErr("Revenue must be a non-negative number."); return; }
    if (weekIsBoundary && (Number(nonPayableRevenue) < 0 || Number(nonPayableRevenue) > Number(revenue))) {
      setErr("Non-payable revenue must be between 0 and the full-week revenue.");
      return;
    }
    if (!weekIsBoundary && Number(nonPayableRevenue) > 0) {
      setErr("Non-payable revenue is only valid on the boundary week (2026-06-28).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/comp/v2/weekly-calc", { week_start: weekStart, revenue: String(revenue) });
      setResult(data);
    } catch (e) {
      setErr(extractApiError(e)?.message || "Calculation failed.");
    } finally { setBusy(false); }
  };

  const saveWeek = async () => {
    setErr(null); setMsg(null);
    if (!result) { setErr("Run the calculation first."); return; }
    setSaveBusy(true);
    try {
      await api.post("/comp/v2/records", { week_start: weekStart, revenue: String(revenue) });
      setMsg(`Saved ${weekStart} — override $${Number(result.override).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`);
      onSaved?.();
    } catch (e) {
      setErr(extractApiError(e)?.message || "Save failed.");
    } finally { setSaveBusy(false); }
  };

  const downloadPdf = async () => {
    setErr(null); setMsg(null);
    if (!result) { setErr("Run the calculation first."); return; }
    setPdfBusy(true);
    try {
      await downloadPdfBlob(
        "/comp/v2/weekly-statement.pdf",
        { week_start: weekStart, revenue: String(revenue), override_paid_ytd: String(overridePaidYtd || "0") },
        `comp-statement-${weekStart}.pdf`
      );
    } catch (e) { setErr(`PDF: ${e.message}`); } finally { setPdfBusy(false); }
  };

  return (
    <section className="bg-white border border-[#e7e9ee] rounded-xl p-6" data-testid="comp-weekly-calc">
      <div className="flex items-center gap-2 mb-4">
        <Calculator size={16} strokeWidth={1.8} className="text-[#0072bc]" />
        <div className="text-[11.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">Weekly calculator</div>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Pay week (Sunday)">
          <input type="date" data-testid="comp-week-start" value={weekStart} onChange={(e) => setWeekStart(e.target.value)}
            min={COMMISSION_BOUNDARY_SUNDAY}
            className="w-full border border-[#d5dae2] rounded-lg px-3 py-2 text-[14px]" />
          {!weekIsSunday && <div className="text-[12px] text-[#b45309] mt-1">Must be a Sunday.</div>}
          {weekIsSunday && !weekIsPayable && (
            <div className="text-[12px] text-[#b45309] mt-1">Commission effective Jul 1, 2026 — pick {COMMISSION_BOUNDARY_SUNDAY} or later.</div>
          )}
        </Field>
        <Field label={weekIsBoundary ? "Full-week revenue ($, Sun–Sat)" : "Weekly revenue ($)"}>
          <input type="number" inputMode="decimal" min="0" step="0.01" data-testid="comp-revenue" value={revenue}
            onChange={(e) => setRevenue(e.target.value)} placeholder="e.g. 79000"
            className="w-full border border-[#d5dae2] rounded-lg px-3 py-2 text-[14px] font-mono-data" />
        </Field>
        {weekIsBoundary ? (
          <Field label="Non-payable revenue ($, Jun 28–30 only)">
            <input type="number" inputMode="decimal" min="0" step="0.01" data-testid="comp-non-payable-revenue" value={nonPayableRevenue}
              onChange={(e) => setNonPayableRevenue(e.target.value)}
              className="w-full border border-[#d5dae2] rounded-lg px-3 py-2 text-[14px] font-mono-data" />
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-1">Revenue for Jun 28–30. Excluded from the override calculation. Payable revenue = full − this.</div>
          </Field>
        ) : (
          <Field label="Override paid YTD ($) — for PDF cap line">
            <input type="number" inputMode="decimal" min="0" step="0.01" data-testid="comp-override-ytd" value={overridePaidYtd}
              onChange={(e) => setOverridePaidYtd(e.target.value)}
              className="w-full border border-[#d5dae2] rounded-lg px-3 py-2 text-[14px] font-mono-data" />
          </Field>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <button data-testid="comp-calc-btn" onClick={calc} disabled={busy || !weekIsSunday || !weekIsPayable}
          className="bg-[#0072bc] text-white text-[13.5px] font-semibold px-4 py-2 rounded-lg hover:bg-[#005f9c] disabled:opacity-60">
          {busy ? "Calculating…" : "Calculate"}
        </button>
        <button data-testid="comp-save-btn" onClick={saveWeek} disabled={saveBusy || !result}
          className="flex items-center gap-2 bg-[#0e8a68] text-white text-[13.5px] font-semibold px-4 py-2 rounded-lg hover:bg-[#0a6b52] disabled:opacity-60">
          <Save size={14} strokeWidth={2} />
          {saveBusy ? "Saving…" : "Save to history"}
        </button>
        <button data-testid="comp-download-pdf" onClick={downloadPdf} disabled={pdfBusy || !result}
          className="flex items-center gap-2 border border-[#d5dae2] text-[#0e1420] text-[13.5px] font-semibold px-4 py-2 rounded-lg hover:bg-[#f5f7fa] disabled:opacity-60">
          <Download size={14} strokeWidth={2} />
          {pdfBusy ? "Generating…" : "Download PDF"}
        </button>
      </div>
      {err && <div data-testid="comp-calc-error" className="mt-3 text-[13px] text-[#7a1c1c]">{err}</div>}
      {msg && <div data-testid="comp-calc-msg" className="mt-3 text-[13px] text-[#0e8a68]">{msg}</div>}
      {result && <CalcResult result={result} />}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[12.5px] font-semibold text-[#0e1420] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function CalcResult({ result }) {
  const override = Number(result.override);
  const above = Number(result.above_floor);
  const nonPayable = Number(result.non_payable_revenue || 0);
  const payable = Number(result.payable_revenue || result.revenue);
  return (
    <div className="mt-6 space-y-4" data-testid="comp-calc-result">
      {result.partial_week && (
        <div className="bg-[#fef3c7] border border-[#f59e0b] rounded-lg px-3 py-2 text-[12.5px] text-[#7c2d12]" data-testid="comp-partial-week-note">
          <b>Boundary week</b> — commission effective Jul 1, 2026. Only {result.day_split?.length} of 7 days count ({result.non_payable_days?.join(", ")} excluded).
          {nonPayable > 0 && (
            <div className="mt-1 font-mono-data">
              Full-week ${Number(result.revenue).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} − Non-payable ${nonPayable.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} = Payable <b>${payable.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</b> (used vs floor)
            </div>
          )}
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-4">
        <Cell
          label={result.partial_week ? "Payable revenue" : "Revenue"}
          value={fmt$(result.partial_week ? payable : result.revenue)}
        />
        <Cell label="Weekly floor" value={fmt$(result.floor)} />
        <Cell label="Above floor" value={above > 0 ? fmt$(above) : "$0.00"} />
        <Cell label={`Override (${Math.round(Number(result.override_rate) * 100)}%)`} value={fmt$(override)} highlight testId="comp-override-value" />
      </div>
      <div className="bg-[#f8fafc] border border-[#e7e9ee] rounded-lg p-4">
        <div className="text-[12px] uppercase tracking-[.08em] text-[color:var(--cc-ink-muted)] font-semibold mb-2">Floor breakdown</div>
        {(result.cross_month || result.partial_week) ? <CrossMonthTable result={result} /> : <InMonthLine result={result} />}
      </div>
    </div>
  );
}

function InMonthLine({ result }) {
  const m = result.months_in_week[0];
  const baseline = result.monthly_baselines_used[m];
  return (
    <div className="text-[13px] text-[#0e1420]" data-testid="comp-in-month-breakdown">
      In-month week ({MONTH_NAMES[m]}) — monthly baseline <span className="font-mono-data font-semibold">{fmt$0(baseline)}</span> ÷ <span className="font-mono-data">{result.in_month_divisor}</span> = <span className="font-mono-data font-semibold">{fmt$(result.floor)}</span>
    </div>
  );
}

function CrossMonthTable({ result }) {
  return (
    <div data-testid="comp-cross-month-breakdown">
      <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mb-2">Cross-month week — per-day floor (Addendum No. 1):</div>
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-left text-[color:var(--cc-ink-muted)]">
            <th className="py-1 pr-3 font-semibold">Date</th>
            <th className="py-1 pr-3 font-semibold">Month</th>
            <th className="py-1 pr-3 font-semibold">Days in month</th>
            <th className="py-1 text-right font-semibold">Daily rate</th>
          </tr>
        </thead>
        <tbody>
          {result.day_split.map((row) => (
            <tr key={row.date} className="border-t border-[#eef1f6]">
              <td className="py-1 pr-3 font-mono-data">{row.date}</td>
              <td className="py-1 pr-3">{MONTH_NAMES[row.month]}</td>
              <td className="py-1 pr-3 font-mono-data">{row.days_in_month}</td>
              <td className="py-1 text-right font-mono-data">${Number(row.daily_rate).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[#0e1420]">
            <td colSpan="3" className="py-2 text-right font-semibold">Sum</td>
            <td className="py-2 text-right font-mono-data font-bold">{fmt$(result.floor)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Cell({ label, value, highlight = false, testId }) {
  return (
    <div data-testid={testId}
      className={`bg-white border rounded-lg p-3 ${highlight ? "border-[#0072bc] ring-1 ring-[#0072bc]/30" : "border-[#e7e9ee]"}`}>
      <div className="text-[11.5px] uppercase tracking-[.08em] text-[color:var(--cc-ink-muted)] font-semibold">{label}</div>
      <div className={`font-mono-data font-bold mt-1 ${highlight ? "text-[22px] text-[#0072bc]" : "text-[18px] text-[#0e1420]"}`}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------- bulk entry
function BulkEntry({ onSaved }) {
  const initialSunday = useMemo(() => toSundayISO(todayISO()), []);
  const [rows, setRows] = useState([
    { week_start: initialSunday, revenue: "", non_payable_revenue: "0" },
  ]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [results, setResults] = useState(null);

  const addRow = () => setRows((r) => [...r, { week_start: "", revenue: "", non_payable_revenue: "0" }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));

  const fillJulyAugust = () => {
    // Start with the boundary week (2026-06-28) — commission effective
    // Jul 1, so this Sunday is the first payable pay-week (partial: only
    // Jul 1–4 count). Then every Sunday through end of August.
    const sundays = ["2026-06-28"];
    const d = new Date(Date.UTC(2026, 6, 5)); // 2026-07-05 Sunday
    while (d.getUTCMonth() === 6 || d.getUTCMonth() === 7) {
      sundays.push(d.toISOString().slice(0, 10));
      d.setUTCDate(d.getUTCDate() + 7);
    }
    setRows(sundays.map((s) => ({ week_start: s, revenue: "", non_payable_revenue: "0" })));
  };

  const submit = async () => {
    setErr(null); setResults(null);
    const cleaned = rows.filter((r) => r.week_start || r.revenue);
    if (cleaned.length === 0) { setErr("Add at least one row."); return; }
    for (let i = 0; i < cleaned.length; i++) {
      const r = cleaned[i];
      const isBoundaryRow = r.week_start === "2026-06-28";
      if (!isValidSunday(r.week_start)) { setErr(`Row ${i + 1}: week start must be a Sunday.`); return; }
      if (!isPayableSunday(r.week_start)) { setErr(`Row ${i + 1}: commission effective Jul 1, 2026 — earliest enterable Sunday is ${COMMISSION_BOUNDARY_SUNDAY}.`); return; }
      if (r.revenue === "" || Number(r.revenue) < 0) { setErr(`Row ${i + 1}: revenue must be a non-negative number.`); return; }
      const np = Number(r.non_payable_revenue || 0);
      if (np < 0 || np > Number(r.revenue)) { setErr(`Row ${i + 1}: non-payable revenue must be between 0 and the full-week revenue.`); return; }
      if (np > 0 && !isBoundaryRow) { setErr(`Row ${i + 1}: non-payable revenue is only valid on 2026-06-28 (the boundary week).`); return; }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/comp/v2/records/bulk", {
        weeks: cleaned.map((r) => ({
          week_start: r.week_start,
          revenue: String(r.revenue),
          non_payable_revenue: String(r.non_payable_revenue || "0"),
        })),
      });
      setResults(data);
      onSaved?.();
    } catch (e) {
      setErr(extractApiError(e)?.message || "Bulk save failed.");
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-white border border-[#e7e9ee] rounded-xl p-6" data-testid="comp-bulk-entry">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ListChecks size={16} strokeWidth={1.8} className="text-[#0072bc]" />
          <div className="text-[11.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">Bulk entry</div>
        </div>
        <button
          onClick={fillJulyAugust}
          data-testid="comp-bulk-prefill-jul-aug"
          className="text-[12.5px] text-[#0072bc] font-semibold hover:underline"
        >
          Prefill Jun 28 boundary + July + August 2026
        </button>
      </div>
      <p className="text-[13px] text-[color:var(--cc-ink-muted)] mb-3">
        One row per pay week. Each row uses the same formula as the single-week calculator (in-month divisor or cross-month day-split). Submit once — every row is calculated and saved to history.
      </p>
      <div className="overflow-x-auto" data-testid="comp-bulk-table">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[color:var(--cc-ink-muted)] border-b border-[#e7e9ee]">
              <th className="py-2 pr-3 font-semibold w-[52px]">#</th>
              <th className="py-2 pr-3 font-semibold">Pay week (Sunday)</th>
              <th className="py-2 pr-3 font-semibold">Weekly revenue ($)</th>
              <th className="py-2 pr-3 font-semibold">Non-payable ($, Jun 28–30 only)</th>
              <th className="py-2 pr-3 font-semibold w-[70px]"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const rowIsBoundary = row.week_start === "2026-06-28";
              return (
              <tr key={i} className="border-b border-[#f2f4f8]">
                <td className="py-2 pr-3 font-mono-data text-[color:var(--cc-ink-muted)]">{i + 1}</td>
                <td className="py-2 pr-3">
                  <input
                    type="date"
                    data-testid={`comp-bulk-week-${i}`}
                    value={row.week_start}
                    min={COMMISSION_BOUNDARY_SUNDAY}
                    onChange={(e) => updateRow(i, "week_start", e.target.value)}
                    className="w-full border border-[#d5dae2] rounded-lg px-2 py-1.5 text-[13px]"
                  />
                  {row.week_start && !isValidSunday(row.week_start) && (
                    <div className="text-[11.5px] text-[#b45309] mt-0.5">Must be a Sunday.</div>
                  )}
                  {row.week_start && isValidSunday(row.week_start) && !isPayableSunday(row.week_start) && (
                    <div className="text-[11.5px] text-[#b45309] mt-0.5">Before Jul 1 effective date.</div>
                  )}
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    placeholder="e.g. 79000"
                    data-testid={`comp-bulk-revenue-${i}`}
                    value={row.revenue}
                    onChange={(e) => updateRow(i, "revenue", e.target.value)}
                    className="w-full border border-[#d5dae2] rounded-lg px-2 py-1.5 text-[13px] font-mono-data"
                  />
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    placeholder={rowIsBoundary ? "e.g. 20000" : "n/a"}
                    disabled={!rowIsBoundary}
                    data-testid={`comp-bulk-non-payable-${i}`}
                    value={row.non_payable_revenue || "0"}
                    onChange={(e) => updateRow(i, "non_payable_revenue", e.target.value)}
                    className="w-full border border-[#d5dae2] rounded-lg px-2 py-1.5 text-[13px] font-mono-data disabled:bg-[#f5f7fa] disabled:text-[#a0a4ab]"
                  />
                </td>
                <td className="py-2 pr-3">
                  <button
                    onClick={() => removeRow(i)}
                    disabled={rows.length === 1}
                    data-testid={`comp-bulk-remove-${i}`}
                    className="text-[#7a1c1c] hover:bg-[#fff5f5] rounded-lg px-2 py-1 disabled:opacity-30"
                    aria-label={`Remove row ${i + 1}`}
                  >
                    <Trash2 size={14} strokeWidth={2} />
                  </button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <button
          onClick={addRow}
          data-testid="comp-bulk-add-row"
          className="flex items-center gap-2 border border-[#d5dae2] text-[#0e1420] text-[13.5px] font-semibold px-3 py-1.5 rounded-lg hover:bg-[#f5f7fa]"
        >
          <Plus size={14} strokeWidth={2} /> Add row
        </button>
        <button
          onClick={submit}
          disabled={busy}
          data-testid="comp-bulk-submit"
          className="flex items-center gap-2 bg-[#0072bc] text-white text-[13.5px] font-semibold px-4 py-2 rounded-lg hover:bg-[#005f9c] disabled:opacity-60"
        >
          {busy ? "Calculating + saving…" : `Calculate & save all (${rows.length})`}
        </button>
      </div>
      {err && <div data-testid="comp-bulk-error" className="mt-3 text-[13px] text-[#7a1c1c]">{err}</div>}
      {results && (
        <div className="mt-4 bg-[#f0fdf4] border border-[#bbf7d0] rounded-lg p-3 text-[13px] text-[#166534]" data-testid="comp-bulk-results">
          Saved <b>{results.saved_count}</b> week{results.saved_count === 1 ? "" : "s"}. Total override this batch: <b className="font-mono-data">${results.records.reduce((s, r) => s + Number(r.override), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- history
function History({ bump, readOnly = false }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const { data } = await api.get("/comp/v2/records", { params });
      setData(data);
    } catch (e) {
      setErr(extractApiError(e)?.message || "Load failed.");
    } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load, bump]);

  const rows = useMemo(() => {
    if (!data?.records) return [];
    const sorted = [...data.records];
    sorted.sort((a, b) => (a.week_start < b.week_start ? -1 : 1) * (sortDir === "asc" ? 1 : -1));
    return sorted;
  }, [data, sortDir]);

  const downloadRangePdf = async () => {
    setErr(null); setPdfBusy(true);
    try {
      const body = {};
      if (from) body.from = from;
      if (to) body.to = to;
      const first = rows[0]?.week_start || "range-start";
      const last = rows[rows.length - 1]?.week_start || "range-end";
      await downloadPdfBlob(
        "/comp/v2/multi-week-statement.pdf",
        body,
        `comp-statements-${first}-to-${last}.pdf`
      );
    } catch (e) { setErr(`PDF: ${e.message}`); } finally { setPdfBusy(false); }
  };

  const deleteRow = async (week_start) => {
    if (!window.confirm(`Delete saved record for ${week_start}? This cannot be undone.`)) return;
    setDeleteBusy(week_start); setErr(null);
    try {
      await api.delete(`/comp/v2/records/${week_start}`);
      await load();
    } catch (e) {
      setErr(extractApiError(e)?.message || "Delete failed.");
    } finally { setDeleteBusy(null); }
  };

  const downloadRowPdf = async (r) => {
    setErr(null);
    try {
      await downloadPdfBlob(
        "/comp/v2/weekly-statement.pdf",
        { week_start: r.week_start, revenue: String(r.revenue), override_paid_ytd: "0" },
        `comp-statement-${r.week_start}.pdf`,
      );
    } catch (e) { setErr(`Row PDF: ${e.message}`); }
  };

  const togglePaid = async (r) => {
    setErr(null);
    try {
      if (r.paid_at) {
        await api.delete(`/comp/v2/records/${r.week_start}/mark-paid`);
      } else {
        const today = new Date().toISOString().slice(0, 10);
        const entered = window.prompt(
          `Mark ${r.week_start} as paid on which date? (YYYY-MM-DD)`,
          today,
        );
        if (!entered) return;
        await api.post(`/comp/v2/records/${r.week_start}/mark-paid`, { paid_at: entered });
      }
      await load();
    } catch (e) { setErr(extractApiError(e)?.message || "Mark-paid failed."); }
  };

  return (
    <section className="bg-white border border-[#e7e9ee] rounded-xl p-6" data-testid="comp-history">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <HistoryIcon size={16} strokeWidth={1.8} className="text-[#0072bc]" />
          <div className="text-[11.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">History</div>
        </div>
        <button onClick={load} data-testid="comp-history-refresh"
          className="flex items-center gap-1.5 text-[12.5px] text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]">
          <RefreshCw size={12} strokeWidth={2} /> Refresh
        </button>
      </div>
      <div className="grid gap-3 md:grid-cols-4 mb-4">
        <Field label="From (any date)">
          <input type="date" data-testid="comp-history-from" value={from} onChange={(e) => setFrom(e.target.value)}
            className="w-full border border-[#d5dae2] rounded-lg px-3 py-2 text-[14px]" />
        </Field>
        <Field label="To (any date)">
          <input type="date" data-testid="comp-history-to" value={to} onChange={(e) => setTo(e.target.value)}
            className="w-full border border-[#d5dae2] rounded-lg px-3 py-2 text-[14px]" />
        </Field>
        <Field label="Sort">
          <select value={sortDir} onChange={(e) => setSortDir(e.target.value)} data-testid="comp-history-sort"
            className="w-full border border-[#d5dae2] rounded-lg px-3 py-2 text-[14px]">
            <option value="asc">Oldest first</option>
            <option value="desc">Newest first</option>
          </select>
        </Field>
        <div className="flex items-end">
          <button onClick={downloadRangePdf} disabled={pdfBusy || rows.length === 0} data-testid="comp-history-pdf"
            className="w-full flex items-center justify-center gap-2 border border-[#d5dae2] text-[#0e1420] text-[13.5px] font-semibold px-3 py-2 rounded-lg hover:bg-[#f5f7fa] disabled:opacity-60">
            <FileDown size={14} strokeWidth={2} />
            {pdfBusy ? "Generating…" : `PDF for ${rows.length || 0} week${rows.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
      {err && <div data-testid="comp-history-error" className="mb-3 text-[13px] text-[#7a1c1c]">{err}</div>}
      {loading && <div className="text-[13px] text-[color:var(--cc-ink-muted)]">Loading…</div>}
      {!loading && data && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="comp-history-table">
              <thead>
                <tr className="text-left text-[color:var(--cc-ink-muted)] border-b border-[#e7e9ee]">
                  <th className="py-2 pr-3 font-semibold">Pay week</th>
                  <th className="py-2 pr-3 font-semibold text-right">Revenue</th>
                  <th className="py-2 pr-3 font-semibold text-right">Floor</th>
                  <th className="py-2 pr-3 font-semibold text-right">Override</th>
                  <th className="py-2 pr-3 font-semibold text-right">Cumulative</th>
                  <th className="py-2 pr-3 font-semibold">Paid</th>
                  <th className="py-2 pr-3 font-semibold w-[130px] text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan="7" className="py-4 text-center text-[color:var(--cc-ink-muted)]">No saved records yet. Save a weekly calc or use bulk entry above.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.week_start} className="border-b border-[#f2f4f8] hover:bg-[#f8fafc]">
                    <td className="py-2 pr-3 font-mono-data">
                      {r.week_start}
                      {r.cross_month && <span className="ml-2 text-[11px] text-[#b45309]">cross</span>}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono-data">{fmt$(r.revenue)}</td>
                    <td className="py-2 pr-3 text-right font-mono-data">{fmt$(r.floor)}</td>
                    <td className="py-2 pr-3 text-right font-mono-data font-bold text-[#0072bc]">{fmt$(r.override)}</td>
                    <td className="py-2 pr-3 text-right font-mono-data">{fmt$(r.cumulative_override)}</td>
                    <td className="py-2 pr-3">
                      {r.paid_at ? (
                        <span className="text-[12px] text-[#0e8a68] font-mono-data" data-testid={`comp-history-paid-${r.week_start}`}>
                          ✓ {r.paid_at}
                        </span>
                      ) : (
                        <span className="text-[12px] text-[color:var(--cc-ink-muted)]">unpaid</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => downloadRowPdf(r)}
                        data-testid={`comp-history-pdf-${r.week_start}`}
                        className="text-[#0072bc] hover:underline mr-2 text-[12px]"
                        title="Download this week's PDF"
                      >PDF</button>
                      {!readOnly && (
                        <>
                          <button
                            onClick={() => togglePaid(r)}
                            data-testid={`comp-history-paid-toggle-${r.week_start}`}
                            className={`hover:underline mr-2 text-[12px] ${r.paid_at ? "text-[#7a1c1c]" : "text-[#0e8a68]"}`}
                            title={r.paid_at ? "Un-mark as paid" : "Mark as paid"}
                          >{r.paid_at ? "Unpay" : "Paid?"}</button>
                          <button
                            onClick={() => deleteRow(r.week_start)}
                            disabled={deleteBusy === r.week_start}
                            data-testid={`comp-history-delete-${r.week_start}`}
                            className="text-[#7a1c1c] hover:bg-[#fff5f5] rounded px-1 py-0.5 disabled:opacity-30"
                            aria-label={`Delete ${r.week_start}`}
                          >
                            <Trash2 size={13} strokeWidth={2} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.totals && (
            <div className="grid gap-3 md:grid-cols-6 mt-5" data-testid="comp-history-totals">
              <Cell label="Total revenue (full)" value={fmt$(data.totals.revenue)} />
              <Cell label="Total payable revenue" value={fmt$(data.totals.payable_revenue || data.totals.revenue)} testId="comp-total-payable-revenue" />
              <Cell label="Total override" value={fmt$(data.totals.override)} />
              <Cell label="Base paid YTD (Term)" value={fmt$(data.totals.base_paid_ytd)} />
              <Cell label="Total cash comp YTD" value={fmt$(data.totals.total_cash)} />
              <Cell label="Cap remaining" value={fmt$(data.totals.cap_remaining)} highlight testId="comp-cap-remaining" />
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- monthly true-up
function MonthlyTrueup({ readOnly = false }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [actual, setActual] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [historyBumper, setHistoryBumper] = useState(0);

  const run = async () => {
    setErr(null); setResult(null);
    if (!actual || Number(actual) < 0) { setErr("Actual revenue must be a non-negative number."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/comp/v2/monthly-trueup", { year: Number(year), month: Number(month), actual_revenue: String(actual) });
      setResult(data);
    } catch (e) { setErr(extractApiError(e)?.message || "True-up failed."); } finally { setBusy(false); }
  };

  const save = async () => {
    setErr(null);
    if (!result) { setErr("Compute first."); return; }
    setSaveBusy(true);
    try {
      await api.post("/comp/v2/monthly-trueup/save", {
        year: Number(year), month: Number(month), actual_revenue: String(actual),
      });
      setHistoryBumper(x => x + 1);
    } catch (e) { setErr(extractApiError(e)?.message || "Save failed."); } finally { setSaveBusy(false); }
  };

  const downloadPdf = async () => {
    setErr(null);
    if (!result) { setErr("Compute the true-up first."); return; }
    setPdfBusy(true);
    try {
      await downloadPdfBlob(
        "/comp/v2/monthly-trueup-statement.pdf",
        { year: Number(year), month: Number(month), actual_revenue: String(actual) },
        `comp-monthly-trueup-${year}-${String(Number(month)).padStart(2, "0")}.pdf`,
      );
    } catch (e) { setErr(`PDF: ${e.message}`); } finally { setPdfBusy(false); }
  };

  return (
    <>
    <section className="bg-white border border-[#e7e9ee] rounded-xl p-6" data-testid="comp-monthly-trueup">
      <div className="flex items-center gap-2 mb-4">
        <Calendar size={16} strokeWidth={1.8} className="text-[#0072bc]" />
        <div className="text-[11.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">Monthly true-up</div>
      </div>
      <p className="text-[13px] text-[color:var(--cc-ink-muted)] mb-3">
        Compares actual month revenue to the baseline. Settle within 15 days of month close vs. the sum of weekly override draws paid for that month.
      </p>
      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Year">
          <input type="number" data-testid="comp-trueup-year" value={year} onChange={(e) => setYear(e.target.value)}
            className="w-full border border-[#d5dae2] rounded-lg px-3 py-2 text-[14px] font-mono-data" />
        </Field>
        <Field label="Month (1–12)">
          <select data-testid="comp-trueup-month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="w-full border border-[#d5dae2] rounded-lg px-3 py-2 text-[14px]">
            {MONTH_NAMES.slice(1).map((n, i) => (
              <option key={i + 1} value={i + 1}>{`${i + 1} — ${n}`}</option>
            ))}
          </select>
        </Field>
        <Field label="Actual month revenue ($)">
          <input type="number" inputMode="decimal" min="0" step="0.01" data-testid="comp-trueup-actual" value={actual}
            onChange={(e) => setActual(e.target.value)} placeholder="e.g. 285000"
            className="w-full border border-[#d5dae2] rounded-lg px-3 py-2 text-[14px] font-mono-data" />
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {!readOnly && (
          <button data-testid="comp-trueup-btn" onClick={run} disabled={busy}
            className="bg-[#0072bc] text-white text-[13.5px] font-semibold px-4 py-2 rounded-lg hover:bg-[#005f9c] disabled:opacity-60">
            {busy ? "Calculating…" : "Compute true-up"}
          </button>
        )}
        {!readOnly && (
          <button data-testid="comp-trueup-save-btn" onClick={save} disabled={saveBusy || !result}
            className="border border-[#d5dae2] text-[#0e1420] text-[13.5px] font-semibold px-4 py-2 rounded-lg hover:bg-[#f5f7fa] disabled:opacity-60">
            {saveBusy ? "Saving…" : "Save to history"}
          </button>
        )}
        {!readOnly && (
          <button data-testid="comp-trueup-download-pdf" onClick={downloadPdf} disabled={pdfBusy || !result}
            className="flex items-center gap-2 border border-[#d5dae2] text-[#0e1420] text-[13.5px] font-semibold px-4 py-2 rounded-lg hover:bg-[#f5f7fa] disabled:opacity-60">
            <Download size={14} strokeWidth={2} />
            {pdfBusy ? "Generating…" : "Download PDF"}
          </button>
        )}
        {readOnly && (
          <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] italic" data-testid="comp-trueup-readonly-note">
            Compute / save disabled — read-only. Download historical PDFs from the history table below.
          </div>
        )}
      </div>
      {err && <div data-testid="comp-trueup-error" className="mt-3 text-[13px] text-[#7a1c1c]">{err}</div>}
      {result && (
        <div className="mt-5 grid gap-3 md:grid-cols-4" data-testid="comp-trueup-result">
          <Cell label="Actual revenue" value={fmt$(result.actual_revenue)} />
          <Cell label="Baseline" value={fmt$(result.monthly_baseline)} />
          <Cell label="Above baseline" value={Number(result.above_baseline) > 0 ? fmt$(result.above_baseline) : "$0.00"} />
          <Cell label="True monthly override" value={fmt$(result.true_monthly_override)} highlight testId="comp-trueup-value" />
        </div>
      )}
    </section>
    <MonthlyTrueupHistory refreshKey={historyBumper} readOnly={readOnly} />
    </>
  );
}


function MonthlyTrueupHistory({ refreshKey, readOnly = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/comp/v2/monthly-trueup/history");
      setRows(data?.records || []);
      setErr(null);
    } catch (e) { setErr(extractApiError(e)?.message || "Failed to load history."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [refreshKey]);

  const remove = async (r) => {
    if (!window.confirm(`Delete saved true-up for ${MONTH_NAMES[r.month]} ${r.year}? This cannot be undone.`)) return;
    try {
      await api.delete(`/comp/v2/monthly-trueup/${r.year}/${r.month}`);
      load();
    } catch (e) { alert(extractApiError(e)?.message || "Delete failed."); }
  };

  const downloadPdf = async (r) => {
    try {
      await downloadPdfBlob(
        "/comp/v2/monthly-trueup-statement.pdf",
        { year: r.year, month: r.month },  // no actual_revenue → server pulls from saved record
        `comp-monthly-trueup-${r.year}-${String(r.month).padStart(2, "0")}.pdf`,
      );
    } catch (e) { alert(`PDF: ${e.message}`); }
  };

  return (
    <section className="bg-white border border-[#e7e9ee] rounded-xl p-6" data-testid="comp-monthly-trueup-history">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">Monthly true-up history</div>
        <button onClick={load} disabled={loading} className="text-[12px] text-[#0072bc] hover:underline disabled:opacity-60" data-testid="comp-trueup-history-refresh">
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      {err && <div className="text-[13px] text-[#7a1c1c] mb-2">{err}</div>}
      {!err && rows.length === 0 && !loading && (
        <div className="text-[13px] text-[color:var(--cc-ink-muted)]" data-testid="comp-trueup-history-empty">No saved true-ups yet.</div>
      )}
      {rows.length > 0 && (
        <table className="w-full text-[13px]" data-testid="comp-trueup-history-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[.06em] text-[color:var(--cc-ink-muted)]">
              <th className="py-2">Month</th>
              <th className="py-2 text-right">Actual revenue</th>
              <th className="py-2 text-right">Settlement</th>
              <th className="py-2">Direction</th>
              <th className="py-2">Computed</th>
              <th className="py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.year}-${r.month}`} className="border-t border-[#e7e9ee]" data-testid={`comp-trueup-history-row-${r.year}-${r.month}`}>
                <td className="py-2 font-medium">{MONTH_NAMES[r.month]} {r.year}</td>
                <td className="py-2 text-right font-mono-data">{fmt$(r.actual_revenue)}</td>
                <td className="py-2 text-right font-mono-data">{fmt$(r.settlement_amount)}</td>
                <td className="py-2">{r.settlement_direction === "owed_to_brett" ? "Owed to Brett" : "Overpayment"}</td>
                <td className="py-2 text-[12px] text-[color:var(--cc-ink-muted)]">
                  {r.computed_at ? new Date(r.computed_at).toLocaleDateString() : ""}
                </td>
                <td className="py-2 text-right">
                  <button onClick={() => downloadPdf(r)} className="text-[#0072bc] hover:underline mr-3" data-testid={`comp-trueup-history-pdf-${r.year}-${r.month}`}>PDF</button>
                  {!readOnly && (
                    <button onClick={() => remove(r)} className="text-[#7a1c1c] hover:underline" data-testid={`comp-trueup-history-delete-${r.year}-${r.month}`}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- formula reference
function FormulaReference({ status }) {
  return (
    <section className="bg-white border border-[#e7e9ee] rounded-xl p-6" data-testid="comp-formula-ref">
      <div className="text-[11.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold mb-3">Formula reference (signed agreement)</div>
      <ol className="space-y-2 text-[13px] text-[#0e1420]">
        <li><span className="font-mono-data text-[color:var(--cc-ink-muted)]">1.</span> <b>Base salary:</b> $60,000/yr flat, $5,000/mo, paid regardless.</li>
        <li><span className="font-mono-data text-[color:var(--cc-ink-muted)]">2.</span> <b>Weekly floor (in-month):</b> monthly baseline ÷ <span className="font-mono-data">{status.in_month_divisor}</span> (Section 4).</li>
        <li><span className="font-mono-data text-[color:var(--cc-ink-muted)]">3.</span> <b>Weekly floor (cross-month):</b> sum of daily rates (baseline ÷ days-in-month) over 7 days (Addendum No. 1).</li>
        <li><span className="font-mono-data text-[color:var(--cc-ink-muted)]">4.</span> <b>Override:</b> {Math.round(Number(status.override_rate) * 100)}% × max(0, revenue − floor). Never negative.</li>
        <li><span className="font-mono-data text-[color:var(--cc-ink-muted)]">5.</span> <b>Monthly true-up:</b> 18% × max(0, actual − baseline), settled within 15 days of close.</li>
        <li><span className="font-mono-data text-[color:var(--cc-ink-muted)]">6.</span> <b>Cap:</b> {fmt$0(status.annual_cap)} total cash comp. When hit, override stops.</li>
      </ol>
    </section>
  );
}
